import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BackupFile, UploadTask } from 'src/app/common/models/BackupFile';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BackupApiService {

  private apiDomain = environment.API_URL_CORE;
  private CHUNK_SIZE = 25 * 1024 * 1024; // 25 MB

  // Keep track of ongoing uploads
  private uploadTasks: { [fileName: string]: UploadTask } = {};

  constructor(private http: HttpClient) { }

  // Start upload (if not already started)
  uploadFile(file: File): Observable<number> {
    const fileName = file.name;

    // If upload already started, return existing observable
    if (this.uploadTasks[fileName]) {
      return this.uploadTasks[fileName].progressSubject.asObservable();
    }

    const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);
    const progressSubject = new BehaviorSubject<number>(0);

    const task: UploadTask = {
      file,
      progressSubject,
      totalChunks,
      currentChunk: 0,
    };

    this.uploadTasks[fileName] = task;

    // Start the upload asynchronously
    this.uploadNextChunk(task);

    return progressSubject.asObservable();
  }

  getAllBackups(): Observable<BackupFile[]> {
    return this.http.get<BackupFile[]>(`${this.apiDomain}/api/backup/list`);
  }

  deleteBackup(fileNames: string[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiDomain}/api/backup/delete`, fileNames);
  }

  downloadBackup(fileName: string): Observable<Blob> {
    return this.http.get(`${this.apiDomain}/api/backup/download`, {
      params: { fileName },
      responseType: 'blob'
    });
  }

  getDownloadUrl(fileName: string): Observable<string> {
    return this.http.get<any>(
      `${this.apiDomain}/api/backup/get-download-url`,
      { params: { fileName } }
    ).pipe(map(res => res.url));
  }

  checkFileExists(fileName: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiDomain}/api/backup/exists?fileName=${encodeURIComponent(fileName)}`);
  }

  cancelUpload(fileName: string) {
    const task = this.uploadTasks[fileName];
    if (task) {
      task.cancelled = true;
    }
  }

  private async uploadNextChunk(task: UploadTask) {
    const { file, progressSubject, totalChunks } = task;

    while (task.currentChunk < totalChunks) {
      if (task.cancelled) {      
        progressSubject.complete();
        delete this.uploadTasks[file.name];
        return;
      }
      const start = task.currentChunk * this.CHUNK_SIZE;
      const end = Math.min(start + this.CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const form = new FormData();
      form.append('chunk', chunk);
      form.append('fileName', file.name);
      form.append('chunkNumber', task.currentChunk.toString());
      form.append('totalChunks', totalChunks.toString());

      try {
        await this.http.post(this.apiDomain + '/api/backup/upload', form).toPromise();
        task.currentChunk++;
        progressSubject.next(Math.round((task.currentChunk / totalChunks) * 100));
      } catch (err) {
        progressSubject.error(err);
        delete this.uploadTasks[file.name];
        return;
      }
    }

    progressSubject.complete();
    delete this.uploadTasks[file.name];
  }

  getProgress(fileName: string): Observable<number> | null {
    return this.uploadTasks[fileName]?.progressSubject.asObservable() || null;
  }
}